import { useEffect } from 'react';
import { apiClient } from '@packages/api-client';

const _configOrigin = (window as any)._intellix_server_config?.parentOrigin;
const ALLOWED_ORIGIN =
  (typeof _configOrigin === 'string' && _configOrigin.startsWith('http') ? _configOrigin : null) ||
  import.meta.env.VITE_PARENT_ORIGIN ||
  'https://intelliu.aialign.cn';

/**
 * 从 URL 参数中获取 token 和 refresh_token
 * 支持参数：token, refresh_token, mode
 */
const getAuthFromUrl = (): { token?: string; refreshToken?: string } | null => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  if (!token) return null;

  return { token, refreshToken: params.get('refresh_token') ?? undefined };
};

/**
 * 检查是否为嵌入模式
 */
const isEmbedMode = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.get('mode') === 'embed' || sessionStorage.getItem('intellix_embed_mode') === 'true';
};

/**
 * 应用从 URL 获取的 token
 */
const applyAuthFromUrl = (payload: { token?: string; refreshToken?: string }) => {
  if (!payload?.token) return;
  // 去掉可能携带的 "Bearer " 前缀，拦截器会统一拼接
  const rawToken = payload.token.replace(/^Bearer\s+/i, '');
  console.log('[useParentAuthSync] 应用 URL token:', rawToken.slice(0, 20) + '...');
  apiClient.setAuthToken(rawToken);

  if (payload.refreshToken) {
    const rawRefreshToken = payload.refreshToken.replace(/^Bearer\s+/i, '');
    apiClient.setRefreshToken(rawRefreshToken);
  }

  window.dispatchEvent(new CustomEvent('auth:parent-sync'));
};

/**
 * 初始化 URL 参数中的 token
 * 仅支持从 URL 获取 token，不再使用 postMessage 登录
 */
const initUrlAuth = () => {
  const authPayload = getAuthFromUrl();
  if (authPayload) {
    console.log('[useParentAuthSync] 从 URL 参数获取到 token，正在应用...');
    applyAuthFromUrl(authPayload);

    // 清理 URL 中的 token / refresh_token 参数（使用 replaceState 避免留下历史记录）
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    url.searchParams.delete('refresh_token');
    // 保留 mode=embed 参数，用于后续判断嵌入模式
    window.history.replaceState({}, '', url.toString());

    return true;
  }
  return false;
};

/**
 * 处理嵌入模式下的未授权情况
 * 通过双向通信通知父项目，由父项目重定向到登录页面
 */
export const handleEmbedUnauthorized = (): void => {
  if (!isEmbedMode()) return;

  console.log('[useParentAuthSync] 嵌入模式检测到登录失效，通知父项目处理...');

  // 清除本地 token
  apiClient.clearAuthToken();

  // 通知父项目登录失效，由父项目处理重定向
  if (window.parent !== window) {
    window.parent.postMessage(
      {
        type: 'INTELLIX_AUTH_EXPIRED',
        payload: {
          message: 'Token expired or invalid',
          currentUrl: window.location.href,
        }
      },
      ALLOWED_ORIGIN
    );
    console.log('[useParentAuthSync] 已通知父项目登录失效');
  }
};

/**
 * 模块加载时立即同步执行 URL token 初始化
 * 确保在任何 React 组件（如 ProtectedRoute）渲染之前，
 * token 已写入 localStorage，避免首次渲染时 isAuthenticated() 返回 false
 */
initUrlAuth();

/**
 * 初始化嵌入模式认证
 * 仅支持从 URL 参数获取 token（用于首次加载时的认证传递）
 * 不再使用 postMessage 进行登录同步
 *
 * 登录失效时通过 postMessage 通知父项目处理重定向
 *
 * 本地开发：VITE_PARENT_ORIGIN=http://localhost:3000（需重启 dev server 生效）
 * 正式环境：VITE_PARENT_ORIGIN=https://intelliu.aialign.cn
 */
export const useParentAuthSync = () => {
  useEffect(() => {
    // 保留作为安全后备，确保 token 被应用
    initUrlAuth();
  }, []);
};

export default useParentAuthSync;

// 重新导出供其他模块使用
export { isEmbedMode };
